/** Pure view rules of the inline Problem alert (no React): message, `missing[]` chips and the other context lists. */

export interface MissingItem {
  key: string;
  /** Why it is listed, unless it is plain "missing" (that is the default and is not shown). */
  reason?: string;
}

export type ContextList = [name: string, values: string[]];

/** The API's own message, else the Error's, else the value as text. */
export function problemMessage(error: unknown, apiMessage: string | undefined): string {
  return apiMessage ?? (error instanceof Error ? error.message : String(error));
}

function isObject(m: unknown): m is object {
  return typeof m === 'object' && m !== null;
}

function missingKey(m: unknown): string {
  if (typeof m === 'string') return m;
  return isObject(m) && 'key' in m ? String((m as { key: unknown }).key) : String(m);
}

function missingReason(m: unknown): string | undefined {
  const reason = isObject(m) && 'reason' in m ? String((m as { reason: unknown }).reason) : undefined;
  return reason && reason !== 'missing' ? reason : undefined;
}

/** `context.missing[]` (HGR-ENV-MISSING-REQUIRED): bare keys or `{ key, reason }` objects. */
export function missingItems(ctx: Record<string, unknown>): MissingItem[] {
  const missing = Array.isArray(ctx.missing) ? ctx.missing : [];
  return missing.map((m) => {
    const reason = missingReason(m);
    return reason === undefined ? { key: missingKey(m) } : { key: missingKey(m), reason };
  });
}

/** The other string lists of the context (`allowedTracks[]`…), in the context's own order. */
export function contextLists(ctx: Record<string, unknown>): ContextList[] {
  return Object.entries(ctx).filter(([k, v]) => k !== 'missing' && Array.isArray(v) && v.every((x) => typeof x === 'string')) as ContextList[];
}

/** Whether there is anything to show under the title (field errors, missing keys, lists or a request id). */
export function hasProblemDetails(parts: { errors: number; missing: number; lists: number; requestId?: string }): boolean {
  return parts.errors > 0 || parts.missing > 0 || parts.lists > 0 || Boolean(parts.requestId);
}
